<script>
/**
 * @component PatientCredential
 * @description Credencial virtual del afiliado. Muestra datos del afiliado,
 *              token con countdown, grupo familiar. Solo visible si el paciente
 *              tiene un Member vinculado (GET /member/me retorna datos).
 * @example <PatientCredential />
 */
import { mapGetters } from "vuex";
import memberService from "@/services/member";

export default {
  name: "PatientCredential",

  data() {
    return {
      member: null,
      token: null,
      family: [],
      isLoading: true,
      isLoadingFamily: false,
      isMember: false,
      countdown: "",
      countdownInterval: null,
      tokenExpiresAt: null,
    };
  },

  computed: {
    ...mapGetters(["user"]),

    /**
     * @description Nombre completo del afiliado
     * @returns {string}
     */
    memberFullName() {
      if (!this.member) return "";
      return `${this.member.lastName || ""}, ${
        this.member.firstName || ""
      }`.trim();
    },

    /**
     * @description Número de afiliado formateado
     * @returns {string}
     */
    memberNumber() {
      return this.member?.memberNumber || "—";
    },

    /**
     * @description Plan del afiliado
     * @returns {string}
     */
    planName() {
      if (!this.member?.plan) return "—";
      if (typeof this.member.plan === "object") {
        return this.member.plan.name || "—";
      }
      return this.member.plan;
    },

    /**
     * @description DNI del afiliado
     * @returns {string}
     */
    memberDni() {
      return this.member?.dni || this.member?.documentNumber || "—";
    },

    /**
     * @description Tipo de integrante del grupo familiar
     * @returns {string}
     */
    familyRole() {
      const role =
        this.member?.familyRole || this.member?.familyGroupType || "";
      const map = {
        titular: "Titular",
        spouse: "Cónyuge",
        child: "Hijo/a",
        conyuge: "Cónyuge",
        hijo: "Hijo/a",
      };
      return map[role.toLowerCase()] || role || "Titular";
    },

    /**
     * @description Código de token para la credencial
     * @returns {string}
     */
    tokenCode() {
      return this.token?.code || this.token?.token || "———";
    },

    /**
     * @description Estado del afiliado
     * @returns {string}
     */
    memberStatus() {
      const status = this.member?.status || "active";
      const map = {
        active: "Afiliado activo",
        inactive: "Inactivo",
        waiting: "En espera",
      };
      return map[status] || status;
    },
  },

  created() {
    this.loadMemberData();
  },

  beforeDestroy() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  },

  methods: {
    /**
     * @description Carga datos del afiliado. Si 404, el paciente no es afiliado.
     */
    async loadMemberData() {
      this.isLoading = true;
      const silentOpts = { getErrorMessage: () => null };
      try {
        const data = await memberService.getMe(silentOpts);
        if (data) {
          this.member = data;
          this.isMember = true;
          this.loadToken();
          this.loadFamily();
        }
      } catch {
        this.isMember = false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Carga el token de credencial y arranca el countdown.
     *              El endpoint GET /members/me/token devuelve `remainingSeconds`
     *              (cuánto le queda al token actual antes de que el server lo
     *              refresque, según el `step` TOTP). Usamos eso para alinear
     *              el countdown con la realidad del server. Si por algún motivo
     *              no viene, fallback a `expiresAt` o a 30s (default TOTP).
     */
    async loadToken() {
      const silentOpts = { getErrorMessage: () => null };
      try {
        const data = await memberService.getMyToken(silentOpts);
        this.token = data;
        if (data?.remainingSeconds != null) {
          this.tokenExpiresAt = new Date(
            Date.now() + data.remainingSeconds * 1000
          );
        } else if (data?.expiresAt) {
          this.tokenExpiresAt = new Date(data.expiresAt);
        } else {
          this.tokenExpiresAt = new Date(Date.now() + 30 * 1000);
        }
        this.startCountdown();
      } catch {
        this.token = null;
      }
    },

    /**
     * @description Carga el grupo familiar del afiliado
     */
    async loadFamily() {
      this.isLoadingFamily = true;
      const silentOpts = { getErrorMessage: () => null };
      try {
        const data = await memberService.getMyFamily(silentOpts);
        this.family = Array.isArray(data) ? data : data?.members || [];
      } catch {
        this.family = [];
      } finally {
        this.isLoadingFamily = false;
      }
    },

    /**
     * @description Inicia el countdown para la renovación del token
     */
    startCountdown() {
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.updateCountdown();
      this.countdownInterval = setInterval(() => {
        this.updateCountdown();
      }, 1000);
    },

    /**
     * @description Actualiza el texto del countdown. Si llega a 0, refresca el token.
     */
    updateCountdown() {
      if (!this.tokenExpiresAt) {
        this.countdown = "";
        return;
      }
      const now = Date.now();
      const diff = this.tokenExpiresAt.getTime() - now;
      if (diff <= 0) {
        this.countdown = "Renovando...";
        this.loadToken();
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      this.countdown = `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    },

    /**
     * @description Devuelve las iniciales de un nombre
     * @param {Object} m - Miembro
     * @returns {string}
     */
    getInitials(m) {
      const first = (m.firstName || "")[0] || "";
      const last = (m.lastName || "")[0] || "";
      return `${first}${last}`.toUpperCase();
    },

    /**
     * @description Devuelve el nombre completo de un miembro del grupo familiar
     * @param {Object} m - Miembro
     * @returns {string}
     */
    getFamilyMemberName(m) {
      return `${m.lastName || ""}, ${m.firstName || ""}`.trim();
    },

    /**
     * @description Devuelve el tag de relación familiar
     * @param {Object} m - Miembro
     * @returns {string}
     */
    getFamilyRole(m) {
      const role = m.familyRole || m.familyGroupType || "";
      const map = {
        titular: "Titular",
        spouse: "Cónyuge",
        child: "Hijo/a",
        conyuge: "Cónyuge",
        hijo: "Hijo/a",
      };
      return map[role.toLowerCase()] || role || "—";
    },

    /**
     * @description Si el miembro es el titular del grupo
     * @param {Object} m - Miembro
     * @returns {boolean}
     */
    isTitular(m) {
      const role = (m.familyRole || m.familyGroupType || "").toLowerCase();
      return role === "titular" || role === "";
    },

    /** @description Placeholder: compartir credencial */
    handleShare() {
      this.$message.info("Función de compartir próximamente disponible.");
    },

    /** @description Placeholder: descargar credencial */
    handleDownload() {
      this.$message.info("Función de descarga próximamente disponible.");
    },

    /** @description Placeholder: ver QR */
    handleQR() {
      this.$message.info("Función de código QR próximamente disponible.");
    },
  },
};
</script>

<template lang="pug">
.pc(v-loading="isLoading")
  template(v-if="isMember && member")
    //- Credential card
    .pc__card
      .pc__card-header
        micon.pc__card-logo(name="quantux-logo-horizontal-nuevo")
        span.pc__card-badge {{ memberStatus }}

      .pc__card-number {{ memberNumber }}

      .pc__card-name {{ memberFullName }}

      .pc__card-details
        .pc__card-detail
          span.pc__card-detail-label Plan
          span.pc__card-detail-value {{ planName }}
        .pc__card-detail
          span.pc__card-detail-label DNI
          span.pc__card-detail-value {{ memberDni }}
        .pc__card-detail
          span.pc__card-detail-label Tipo
          span.pc__card-detail-value {{ familyRole }}

      //- Token section
      .pc__token(v-if="token")
        .pc__token-code {{ tokenCode }}
        .pc__token-countdown(v-if="countdown")
          micon(name="pending_actions")
          span Se renueva en {{ countdown }}

    //- Action buttons
    .pc__actions
      button.pc__action-btn(@click="handleShare")
        micon(name="share")
        span Compartir
      button.pc__action-btn(@click="handleDownload")
        micon(name="download")
        span Descargar
      button.pc__action-btn(@click="handleQR")
        micon(name="qr_code_2")
        span Ver QR

    //- Family group
    .pc__family(v-if="family.length > 0")
      .pc__family-header
        h2.pc__family-title Grupo familiar
        span.pc__family-badge {{ family.length }}

      div(v-loading="isLoadingFamily")
        .pc__family-member(v-for="m in family" :key="m._id || m.memberNumber")
          .pc__family-avatar {{ getInitials(m) }}
          .pc__family-info
            span.pc__family-name {{ getFamilyMemberName(m) }}
            span.pc__family-sub DNI {{ m.dni || m.documentNumber || '—' }} · Nº {{ m.memberNumber || '—' }}
          span.pc__family-tag(:class="{ 'pc__family-tag--titular': isTitular(m) }") {{ getFamilyRole(m) }}

  //- Not a member
  template(v-else-if="!isLoading && !isMember")
    .pc__empty
      micon.pc__empty-icon(name="badge")
      p.pc__empty-text No tenés una credencial de afiliado vinculada a tu cuenta.
</template>

<style lang="scss" scoped>
.pc {
  max-width: 800px;
  margin: 0 auto;

  // ─── Credential card ───
  &__card {
    background: linear-gradient(135deg, #002d5e 0%, #1a4a7a 50%, #2a6db5 100%);
    border-radius: 16px;
    padding: 24px;
    color: #fff;
    position: relative;
    overflow: hidden;
    margin-bottom: 16px;

    &::after {
      content: "";
      position: absolute;
      top: -40px;
      right: -40px;
      width: 160px;
      height: 160px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.04);
      pointer-events: none;
    }
  }

  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  &__card-logo {
    width: 100px;
    fill: #fff;
    opacity: 0.9;
  }

  &__card-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.15);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    backdrop-filter: blur(4px);
  }

  &__card-number {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 2px;
    margin-bottom: 4px;
    font-family: "Plus Jakarta Sans", monospace;
  }

  &__card-name {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 16px;
    opacity: 0.95;
  }

  &__card-details {
    display: flex;
    gap: 24px;
    margin-bottom: 20px;
  }

  &__card-detail {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__card-detail-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.6;
  }

  &__card-detail-value {
    font-size: 13px;
    font-weight: 600;
  }

  // ─── Token ───
  &__token {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 12px 16px;
    backdrop-filter: blur(4px);
  }

  &__token-code {
    font-size: 32px;
    font-weight: 700;
    letter-spacing: 4px;
    font-family: "Plus Jakarta Sans", monospace;
  }

  &__token-countdown {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    opacity: 0.7;

    .micon {
      width: 14px;
      height: 14px;
      fill: #fff;
    }
  }

  // ─── Action buttons ───
  &__actions {
    display: flex;
    gap: 8px;
    margin-bottom: 28px;
  }

  &__action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: $navy;
    transition: all 0.15s ease;

    .micon {
      width: 16px;
      height: 16px;
      fill: $navy;
    }

    &:hover {
      border-color: #0d9d9d;
      background: #f0fdfa;
      color: #0d9d9d;

      .micon {
        fill: #0d9d9d;
      }
    }
  }

  // ─── Family group ───
  &__family {
    margin-bottom: 24px;
  }

  &__family-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  &__family-title {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }

  &__family-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
  }

  &__family-member {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    margin-bottom: 8px;
  }

  &__family-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    flex-shrink: 0;
  }

  &__family-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__family-name {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__family-sub {
    font-size: 11px;
    color: #6b7280;
  }

  &__family-tag {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    color: #6b7280;
    flex-shrink: 0;

    &--titular {
      background: $navy;
      color: #fff;
    }
  }

  // ─── Empty state ───
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    border: 2px dashed #d1d5db;
    border-radius: 10px;
    background: #fff;
  }

  &__empty-icon {
    width: 48px;
    height: 48px;
    fill: #9ca3af;
    margin-bottom: 12px;
  }

  &__empty-text {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    text-align: center;
  }
}

/* ─── Mobile responsive ─── */
@media (max-width: 767px) {
  .pc {
    &__card {
      padding: 20px 16px;
      border-radius: 12px;
    }

    &__card-number {
      font-size: 20px;
      letter-spacing: 1px;
    }

    &__card-name {
      font-size: 16px;
    }

    &__card-details {
      flex-wrap: wrap;
      gap: 12px 20px;
    }

    &__token-code {
      font-size: 24px;
      letter-spacing: 2px;
    }

    &__actions {
      gap: 6px;
    }

    &__action-btn {
      padding: 8px 8px;
      font-size: 11px;

      .micon {
        width: 14px;
        height: 14px;
      }
    }

    &__family-member {
      padding: 10px 12px;
    }

    &__empty {
      padding: 32px 16px;
    }
  }
}
</style>
